import { normalizeContentTagRef } from './contentTags.js';

/** Digital visiting card — layout + contact button templates (shared FE/BE). */

export const VISITING_CARD_EXTENSION_KEY = 'digital_visiting_card';

export const VISITING_CARD_LAYOUT_IDS = Object.freeze(['sheet', 'flip', 'slider']);
export const DEFAULT_VISITING_CARD_LAYOUT = 'sheet';

export const VISITING_CARD_BUTTON_IDS = Object.freeze([
  'dock',
  'keypad',
  'typewriter',
  'labeled_bars',
  'orbit',
  'pill_row',
  'split_cta',
  'stamp_grid',
]);
export const DEFAULT_VISITING_CARD_BUTTONS = 'dock';

export const VISITING_CARD_CATALOG_MODE_IDS = Object.freeze(['manual', 'random', 'by_category']);
export const DEFAULT_VISITING_CARD_CATALOG_MODE = 'manual';

export const VISITING_CARD_CATALOG_COUNTS = Object.freeze([3, 6, 9]);
export const DEFAULT_VISITING_CARD_CATALOG_COUNT = 9;

export const VISITING_CARD_CATALOG_PRESENTATION_IDS = Object.freeze(['grid', 'carousel']);
export const DEFAULT_VISITING_CARD_CATALOG_PRESENTATION = 'grid';

/** How each work/project card looks (independent of grid vs carousel). */
export const VISITING_CARD_PRODUCT_CARD_LAYOUT_IDS = Object.freeze([
  'media_top',
  'media_left',
  'media_right',
  'media_center',
  'media_center_plain',
  'media_only',
]);
export const DEFAULT_VISITING_CARD_PRODUCT_CARD_LAYOUT = 'media_top';

/**
 * Where a tap on a work photo goes. Services always open their own external link,
 * so this setting shapes works and the hero portrait only.
 */
export const VISITING_CARD_PHOTO_LINK_IDS = Object.freeze([
  'none',
  'preview',
  'storefront',
  'product',
]);
export const DEFAULT_VISITING_CARD_PHOTO_LINK = 'preview';

/** Hero / BIO portrait frame on /c (non-orbit). Always contain; never cover-crop. */
export const VISITING_CARD_HERO_PHOTO_ASPECT_IDS = Object.freeze([
  '1_1',
  '2_3',
  '4_3',
  '4_5',
  'as_is',
]);
export const DEFAULT_VISITING_CARD_HERO_PHOTO_ASPECT = '4_3';

/** Section title alignment on /c. `start` = LTR left / HE RTL; `center` always centered. */
export const VISITING_CARD_TITLE_ALIGN_IDS = Object.freeze(['start', 'center']);
export const DEFAULT_VISITING_CARD_TITLE_ALIGN = 'start';

/** Default section titles (RU/EN/HE). Clear a locale → spacer on /c for that lang. */
export const DEFAULT_VISITING_CARD_WORKS_TITLE_I18N = Object.freeze({
  ru: 'Работы',
  en: 'Works',
  he: 'יצירות',
});
export const DEFAULT_VISITING_CARD_SERVICES_TITLE_I18N = Object.freeze({
  ru: 'Услуги',
  en: 'Services',
  he: 'שירותים',
});

/** Manual / random picker ceiling (same as count options). */
export const MAX_VISITING_CARD_PRODUCTS = 9;

/** Services are picked independently of works, so they have their own ceiling. */
export const MAX_VISITING_CARD_SERVICES = 12;

/** by_category: max works on the whole card. */
export const MAX_VISITING_CARD_BY_CATEGORY_TOTAL = 24;

/** by_category: max works per category before moving on. */
export const DEFAULT_VISITING_CARD_PER_CATEGORY = 6;

export const VISITING_CARD_LAYOUT_LABEL_KEYS = Object.freeze({
  sheet: 'visitingCard.layout.sheet',
  flip: 'visitingCard.layout.flip',
  slider: 'visitingCard.layout.slider',
});

export const VISITING_CARD_BUTTON_LABEL_KEYS = Object.freeze({
  dock: 'visitingCard.buttons.dock',
  keypad: 'visitingCard.buttons.keypad',
  typewriter: 'visitingCard.buttons.typewriter',
  labeled_bars: 'visitingCard.buttons.labeledBars',
  orbit: 'visitingCard.buttons.orbit',
  pill_row: 'visitingCard.buttons.pillRow',
  split_cta: 'visitingCard.buttons.splitCta',
  stamp_grid: 'visitingCard.buttons.stampGrid',
});

export const VISITING_CARD_CATALOG_MODE_LABEL_KEYS = Object.freeze({
  manual: 'visitingCard.catalogMode.manual',
  random: 'visitingCard.catalogMode.random',
  by_category: 'visitingCard.catalogMode.byCategory',
});

export const VISITING_CARD_CATALOG_PRESENTATION_LABEL_KEYS = Object.freeze({
  grid: 'visitingCard.catalogPresentation.grid',
  carousel: 'visitingCard.catalogPresentation.carousel',
});

export const VISITING_CARD_PHOTO_LINK_LABEL_KEYS = Object.freeze({
  none: 'visitingCard.photoLink.none',
  preview: 'visitingCard.photoLink.preview',
  storefront: 'visitingCard.photoLink.storefront',
  product: 'visitingCard.photoLink.product',
});

export const VISITING_CARD_HERO_PHOTO_ASPECT_LABEL_KEYS = Object.freeze({
  '1_1': 'visitingCard.heroPhotoAspect.1_1',
  '2_3': 'visitingCard.heroPhotoAspect.2_3',
  '4_3': 'visitingCard.heroPhotoAspect.4_3',
  '4_5': 'visitingCard.heroPhotoAspect.4_5',
  as_is: 'visitingCard.heroPhotoAspect.asIs',
});

export const VISITING_CARD_TITLE_ALIGN_LABEL_KEYS = Object.freeze({
  start: 'visitingCard.titleAlign.start',
  center: 'visitingCard.titleAlign.center',
});

export const VISITING_CARD_PRODUCT_CARD_LAYOUT_LABEL_KEYS = Object.freeze({
  media_top: 'visitingCard.productCard.mediaTop',
  media_left: 'visitingCard.productCard.mediaLeft',
  media_right: 'visitingCard.productCard.mediaRight',
  media_center: 'visitingCard.productCard.mediaCenter',
  media_center_plain: 'visitingCard.productCard.mediaCenterPlain',
  media_only: 'visitingCard.productCard.mediaOnly',
});

/** Landing contacts the card can hide. An id missing from hiddenContactIds stays on. */
export const VISITING_CARD_CONTACT_IDS = Object.freeze([
  'phone',
  'whatsapp',
  'email',
  'instagram',
  'facebook',
  'tiktok',
  'youtube',
  'telegram',
  'pinterest',
  'website',
]);

/**
 * @param {unknown} value
 * @returns {'sheet'|'flip'|'slider'}
 */
export function normalizeVisitingCardLayout(value) {
  const raw = String(value || '').trim();
  if (VISITING_CARD_LAYOUT_IDS.includes(raw)) {
    return raw;
  }
  return DEFAULT_VISITING_CARD_LAYOUT;
}

/**
 * @param {unknown} value
 * @returns {typeof VISITING_CARD_BUTTON_IDS[number]}
 */
export function normalizeVisitingCardButtons(value) {
  const raw = String(value || '').trim();
  if (VISITING_CARD_BUTTON_IDS.includes(raw)) {
    return raw;
  }
  return DEFAULT_VISITING_CARD_BUTTONS;
}

/**
 * @param {unknown} value
 * @returns {'manual'|'random'|'by_category'}
 */
export function normalizeVisitingCardCatalogMode(value) {
  const raw = String(value || '').trim();
  if (VISITING_CARD_CATALOG_MODE_IDS.includes(raw)) {
    return raw;
  }
  return DEFAULT_VISITING_CARD_CATALOG_MODE;
}

/**
 * @param {unknown} value
 * @returns {3|6|9}
 */
export function normalizeVisitingCardCatalogCount(value) {
  const n = Number(value);
  if (VISITING_CARD_CATALOG_COUNTS.includes(n)) {
    return n;
  }
  return DEFAULT_VISITING_CARD_CATALOG_COUNT;
}

/**
 * @param {unknown} value
 * @returns {'grid'|'carousel'}
 */
export function normalizeVisitingCardCatalogPresentation(value) {
  const raw = String(value || '').trim();
  if (VISITING_CARD_CATALOG_PRESENTATION_IDS.includes(raw)) {
    return raw;
  }
  return DEFAULT_VISITING_CARD_CATALOG_PRESENTATION;
}

/**
 * @param {unknown} value
 * @returns {'media_top'|'media_left'|'media_right'|'media_center'|'media_center_plain'|'media_only'}
 */
export function normalizeVisitingCardProductCardLayout(value) {
  const raw = String(value || '').trim();
  if (VISITING_CARD_PRODUCT_CARD_LAYOUT_IDS.includes(raw)) {
    return raw;
  }
  return DEFAULT_VISITING_CARD_PRODUCT_CARD_LAYOUT;
}

/**
 * @param {unknown} value
 * @returns {'none'|'preview'|'storefront'|'product'}
 */
export function normalizeVisitingCardPhotoLink(value) {
  const raw = String(value || '').trim();
  if (VISITING_CARD_PHOTO_LINK_IDS.includes(raw)) {
    return raw;
  }
  return DEFAULT_VISITING_CARD_PHOTO_LINK;
}

/**
 * Card-only tenants have no storefront: storefront/product taps become fullscreen preview.
 * @param {{ photoLink?: unknown, cardOnly?: boolean }} config
 * @returns {'none'|'preview'|'storefront'|'product'}
 */
export function effectiveVisitingCardPhotoLink(config = {}) {
  const link = normalizeVisitingCardPhotoLink(config.photoLink);
  if (config.cardOnly && (link === 'storefront' || link === 'product')) {
    return 'preview';
  }
  return link;
}

/**
 * @param {unknown} value
 * @returns {'1_1'|'2_3'|'4_3'|'4_5'|'as_is'}
 */
export function normalizeVisitingCardHeroPhotoAspect(value) {
  const raw = String(value || '').trim();
  if (VISITING_CARD_HERO_PHOTO_ASPECT_IDS.includes(raw)) {
    return raw;
  }
  return DEFAULT_VISITING_CARD_HERO_PHOTO_ASPECT;
}

/**
 * @param {unknown} value
 * @returns {'start'|'center'}
 */
export function normalizeVisitingCardTitleAlign(value) {
  const raw = String(value || '').trim();
  if (VISITING_CARD_TITLE_ALIGN_IDS.includes(raw)) {
    return raw;
  }
  return DEFAULT_VISITING_CARD_TITLE_ALIGN;
}

/**
 * @param {unknown} input
 * @returns {string[]}
 */
export function normalizeVisitingCardContactIds(input) {
  if (!Array.isArray(input)) return [];
  const allowed = new Set(VISITING_CARD_CONTACT_IDS);
  const seen = new Set();
  const out = [];
  for (const raw of input) {
    const id = String(raw || '').trim();
    if (!allowed.has(id) || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
  }
  return out;
}

/**
 * @param {unknown} input
 * @returns {string[]}
 */
export function normalizeVisitingCardNoteTags(input) {
  if (!Array.isArray(input)) return [];
  const seen = new Set();
  const out = [];
  for (const raw of input) {
    const tag = normalizeContentTagRef(raw);
    if (!tag || seen.has(tag)) continue;
    seen.add(tag);
    out.push(tag);
    if (out.length >= 12) break;
  }
  return out;
}

/**
 * @param {unknown} input
 * @param {number} [max]
 * @returns {string[]}
 */
export function normalizeVisitingCardIdList(input, max = MAX_VISITING_CARD_PRODUCTS) {
  if (!Array.isArray(input)) {
    return [];
  }
  const seen = new Set();
  const out = [];
  for (const raw of input) {
    const id = String(raw || '').trim();
    if (!id || seen.has(id)) continue;
    seen.add(id);
    out.push(id);
    if (out.length >= max) break;
  }
  return out;
}

/** @deprecated Use normalizeVisitingCardIdList */
export function normalizeVisitingCardProductIds(input) {
  return normalizeVisitingCardIdList(input, MAX_VISITING_CARD_PRODUCTS);
}

export const DEFAULT_VISITING_CARD_WORKS_ORDER = 1;
export const DEFAULT_VISITING_CARD_SERVICES_ORDER = 2;

/**
 * @param {unknown} value
 * @param {number} fallback
 * @returns {number}
 */
export function normalizeVisitingCardSectionOrder(value, fallback) {
  const n = Number(value);
  if (!Number.isFinite(n)) {
    return fallback;
  }
  return Math.min(99, Math.max(0, Math.floor(n)));
}

/**
 * @param {{ worksOrder: number, servicesOrder: number }} config
 * @returns {Array<'works'|'services'>}
 */
export function visitingCardCatalogKindsInOrder(config) {
  return (
    /** @type {Array<{ kind: 'works'|'services', order: number }>} */ ([
      { kind: 'works', order: Number(config.worksOrder) || DEFAULT_VISITING_CARD_WORKS_ORDER },
      { kind: 'services', order: Number(config.servicesOrder) || DEFAULT_VISITING_CARD_SERVICES_ORDER },
    ])
  )
    .sort((a, b) => {
      if (a.order !== b.order) return a.order - b.order;
      return a.kind === 'works' ? -1 : 1;
    })
    .map((item) => item.kind);
}

/**
 * @param {unknown} value
 * @param {{ ru: string, en: string, he: string }} defaults
 * @returns {{ ru: string, en: string, he: string }}
 */
export function normalizeVisitingCardTitleI18n(value, defaults) {
  if (typeof value === 'string') {
    // Legacy single string — keep the same text in every locale.
    return { ru: value, en: value, he: value };
  }
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    const src = /** @type {Record<string, unknown>} */ (value);
    return {
      ru: src.ru != null ? String(src.ru) : '',
      en: src.en != null ? String(src.en) : '',
      he: src.he != null ? String(src.he) : '',
    };
  }
  return {
    ru: defaults.ru,
    en: defaults.en,
    he: defaults.he,
  };
}

/**
 * @param {Record<string, unknown>|null|undefined} input
 */
export function normalizeVisitingCardConfig(input = {}) {
  const src = input && typeof input === 'object' ? input : {};
  const catalogMode = normalizeVisitingCardCatalogMode(src.catalogMode ?? src.catalog_mode);
  const catalogCount = normalizeVisitingCardCatalogCount(src.catalogCount ?? src.catalog_count);
  const showStorefrontLink = src.showStorefrontLink !== false && src.show_storefront_link !== false;
  /** Card-only mode: storefront `/` → `/c`. Mutually exclusive with the storefront button. */
  const cardOnly = Boolean(src.cardOnly ?? src.card_only) && !showStorefrontLink;
  return {
    layout: normalizeVisitingCardLayout(src.layout),
    contactButtons: normalizeVisitingCardButtons(src.contactButtons ?? src.contact_buttons),
    showBio: src.showBio !== false && src.show_bio !== false,
    showContacts: src.showContacts !== false && src.show_contacts !== false,
    showCatalog: src.showCatalog !== false && src.show_catalog !== false,
    /** Default on so linked promo items stay visible after split lands. */
    showServices: src.showServices !== false && src.show_services !== false,
    showStorefrontLink,
    cardOnly,
    worksTitle: normalizeVisitingCardTitleI18n(
      src.worksTitle ?? src.works_title,
      DEFAULT_VISITING_CARD_WORKS_TITLE_I18N,
    ),
    servicesTitle: normalizeVisitingCardTitleI18n(
      src.servicesTitle ?? src.services_title,
      DEFAULT_VISITING_CARD_SERVICES_TITLE_I18N,
    ),
    worksTitleAlign: normalizeVisitingCardTitleAlign(
      src.worksTitleAlign ?? src.works_title_align,
    ),
    servicesTitleAlign: normalizeVisitingCardTitleAlign(
      src.servicesTitleAlign ?? src.services_title_align,
    ),
    worksOrder: normalizeVisitingCardSectionOrder(
      src.worksOrder ?? src.works_order,
      DEFAULT_VISITING_CARD_WORKS_ORDER,
    ),
    servicesOrder: normalizeVisitingCardSectionOrder(
      src.servicesOrder ?? src.services_order,
      DEFAULT_VISITING_CARD_SERVICES_ORDER,
    ),
    catalogMode,
    catalogCount,
    catalogPresentation: normalizeVisitingCardCatalogPresentation(
      src.catalogPresentation ?? src.catalog_presentation,
    ),
    productCardLayout: normalizeVisitingCardProductCardLayout(
      src.productCardLayout ?? src.product_card_layout,
    ),
    photoLink: normalizeVisitingCardPhotoLink(src.photoLink ?? src.photo_link),
    /** Missing means on, so cards saved before this flag still open the album. */
    worksTapPreview: src.worksTapPreview !== false && src.works_tap_preview !== false,
    heroPhotoAspect: normalizeVisitingCardHeroPhotoAspect(
      src.heroPhotoAspect ?? src.hero_photo_aspect,
    ),
    /** Works only — services live in serviceIds and never share this budget. */
    productIds: normalizeVisitingCardIdList(
      src.productIds ?? src.product_ids,
      catalogCount,
    ),
    /** Empty = all linked products, same convention as categoryIds. */
    serviceIds: normalizeVisitingCardIdList(
      src.serviceIds ?? src.service_ids,
      MAX_VISITING_CARD_SERVICES,
    ),
    /** Empty = all categories. */
    categoryIds: normalizeVisitingCardIdList(
      src.categoryIds ?? src.category_ids,
      100,
    ),
    perCategoryLimit: Math.min(
      12,
      Math.max(1, Number(src.perCategoryLimit ?? src.per_category_limit) || DEFAULT_VISITING_CARD_PER_CATEGORY),
    ),
    pwaShortcutName: String(src.pwaShortcutName ?? src.pwa_shortcut_name ?? '').trim().slice(0, 48),
    pwaIconUrl: String(src.pwaIconUrl ?? src.pwa_icon_url ?? '').trim(),
    /** Optional my: tag — overrides Settings BIO text on /c when resolvable. */
    bioContentTag: normalizeContentTagRef(src.bioContentTag ?? src.bio_content_tag),
    hiddenContactIds: normalizeVisitingCardContactIds(src.hiddenContactIds ?? src.hidden_contact_ids),
    serviceNoteTags: normalizeVisitingCardNoteTags(src.serviceNoteTags ?? src.service_note_tags),
  };
}

/** UTC calendar day key for 24h-stable random picks. */
export function visitingCardUtcDayKey(date = new Date()) {
  return date.toISOString().slice(0, 10);
}

/**
 * Deterministic shuffle (Mulberry32) — same seed → same order for 24h.
 * @template T
 * @param {T[]} items
 * @param {string} seedStr
 * @returns {T[]}
 */
export function seededShuffle(items, seedStr) {
  const list = Array.isArray(items) ? [...items] : [];
  let h = 2166136261;
  const seed = String(seedStr || '');
  for (let i = 0; i < seed.length; i += 1) {
    h ^= seed.charCodeAt(i);
    h = Math.imul(h, 16777619);
  }
  let state = h >>> 0;
  const rand = () => {
    state |= 0;
    state = (state + 0x6d2b79f5) | 0;
    let t = Math.imul(state ^ (state >>> 15), 1 | state);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
  for (let i = list.length - 1; i > 0; i -= 1) {
    const j = Math.floor(rand() * (i + 1));
    const tmp = list[i];
    list[i] = list[j];
    list[j] = tmp;
  }
  return list;
}
